"use client"

import {
  navigateWithThreadPreservation,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EntityDetailSkeleton,
  PageContainer,
  PageHeader,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  PageHeaderLeading,
  PageHeaderStack,
  PageLineTabs,
  useArtifactCanvas,
  briefPriorityBadgeVariant,
  briefStatusBadgeVariant,
  formatBriefDate,
  formatBriefPriority,
  formatBriefStatus,
} from '@yeshuman/sdk'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { useBrief, useDeleteBrief, useBriefCandidates } from '../../hooks/use-briefs'
import { BriefCanvasForm } from './brief-canvas-form'
import { BriefSearchTab } from './brief-search-tab'
import { BriefCaseFileSections } from './brief-case-file-sections'
import { BriefSectionEditAction } from './brief-section-edit-action'
import { ChartNoAxesColumn, FileText, MapPin, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

type BriefDetailView = 'brief' | 'search'

const BRIEF_DETAIL_TAB_ITEMS = [
  { value: 'brief' as const, label: 'Brief', icon: FileText },
  { value: 'search' as const, label: 'Analysis', icon: ChartNoAxesColumn },
] as const

function briefDetailViewFromParams(searchParams: URLSearchParams): BriefDetailView {
  return searchParams.get('tab') === 'search' ? 'search' : 'brief'
}

export function BriefDetailPage() {
  const { briefId } = useParams<{ briefId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const { openCanvas, closeCanvas } = useArtifactCanvas()

  const activeView = briefDetailViewFromParams(searchParams)

  const setActiveView = useCallback(
    (view: BriefDetailView) => {
      const next = new URLSearchParams(searchParams)
      if (view === 'search') next.set('tab', 'search')
      else next.delete('tab')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const { brief, isLoading, error } = useBrief(briefId || null)
  const { candidates, isLoading: candidatesLoading } = useBriefCandidates(briefId || null)
  const { deleteBrief, isDeleting } = useDeleteBrief()

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const openEditCanvas = useCallback(() => {
    if (!brief) return
    openCanvas({
      title: 'Edit brief',
      preventAutoFocus: true,
      content: (
        <BriefCanvasForm
          key={brief.id}
          mode="edit"
          brief={brief}
          onClose={closeCanvas}
          onSaved={() => {
            closeCanvas()
          }}
        />
      ),
    })
  }, [brief, closeCanvas, openCanvas])

  const handleDelete = () => {
    if (briefId) {
      deleteBrief(briefId, {
        onSuccess: () => {
          toast.success('Brief deleted successfully')
          navigateWithThreadPreservation(navigate, '/recruiter/briefs', searchParams)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to delete brief')
        },
      })
    }
  }

  if (isLoading) {
    return <EntityDetailSkeleton variant="job" ariaLabel="Loading brief" />
  }

  if (error || !brief) {
    return (
      <PageContainer>
        <PageHeaderLeading back={{ label: 'Back to briefs', to: '/recruiter/briefs' }} />
        <div className="text-center py-12">
          <p className="text-destructive">
            {error ? (error as Error).message : 'Brief not found'}
          </p>
          <div className="mt-4">
            <Button onClick={() => navigateWithThreadPreservation(navigate, '/recruiter/briefs', searchParams)}>
              Back to briefs
            </Button>
          </div>
        </div>
      </PageContainer>
    )
  }

  const clientLabel = brief.client_organisation_name?.trim() || 'Speculative'
  const candidateCount = brief.saved_candidates_count ?? candidates.length

  return (
    <PageContainer>
      <div>
        <PageHeaderStack compact>
          <PageHeader
            leading={<PageHeaderLeading back={{ label: 'Back to briefs', to: '/recruiter/briefs' }} />}
            title={brief.title}
            description={clientLabel}
          />

          <PageLineTabs
            value={activeView}
            onValueChange={(value) => setActiveView(value as BriefDetailView)}
            ariaLabel="Brief sections"
            items={BRIEF_DETAIL_TAB_ITEMS}
          />
        </PageHeaderStack>

        <div className="space-y-6">
        <Card className="group/section relative z-0">
          <CardHeader showTitle={false}>
            <BriefSectionEditAction label="Edit brief details" onClick={openEditCanvas} />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex min-w-0 w-full flex-col items-start gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={briefStatusBadgeVariant(brief.status)}>{formatBriefStatus(brief.status)}</Badge>
                <Badge variant={briefPriorityBadgeVariant(brief.priority)}>{formatBriefPriority(brief.priority)}</Badge>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              {brief.location?.trim() ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3.5 shrink-0" aria-hidden />
                  {brief.location}
                </span>
              ) : null}
              {brief.location?.trim() ? <span aria-hidden>·</span> : null}
              <span>Updated {formatBriefDate(brief.updated_at)}</span>
              <span aria-hidden>·</span>
              <span>
                {candidateCount} {candidateCount === 1 ? 'candidate' : 'candidates'}
              </span>
            </div>
          </CardContent>
        </Card>

        {activeView === 'brief' ? (
          <BriefCaseFileSections
            brief={brief}
            candidates={candidates}
            candidatesLoading={candidatesLoading}
            onEditBrief={openEditCanvas}
            onSwitchToSearchTab={() => setActiveView('search')}
          />
        ) : (
          <BriefSearchTab brief={brief} onEditBrief={openEditCanvas} />
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete brief</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this brief? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </div>
      </div>
    </PageContainer>
  )
}
